import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {ExpertWalletService} from '@services/expert-wallet.service';
import {ExpertWalletAccessState} from '@services/expert-wallet-access.state';
import {NotificationService} from '@services/frontend/notification.service';
import {UserService} from '@services/user.service';
import {
  ExpertWalletRecipientPreview,
  ExpertWalletSendResponse,
} from '@models/expert-wallet.interface';
import {httpErrorMessage} from 'app/utils/http-error.utils';
import {forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';

@Component({
  selector: 'app-expert-wallet-qr',
  imports: [RouterLink],
  templateUrl: './expert-wallet-qr.html',
})
export class ExpertWalletQr implements OnInit {
  private readonly expertWalletService = inject(ExpertWalletService);
  private readonly accessState = inject(ExpertWalletAccessState);
  private readonly notificationService = inject(NotificationService);
  private readonly userService = inject(UserService);

  loading = signal(true);
  hasAccess = signal(false);
  sending = signal(false);
  lastResult = signal<ExpertWalletSendResponse | null>(null);
  recipientsPreview = signal<ExpertWalletRecipientPreview[]>([]);
  recipientsLoading = signal(false);

  cabinetName = signal<string | null>(null);
  cabinetPhone = signal<string | null>(null);
  cabinetEmail = signal<string | null>(null);

  hasCabinetName = computed(() => !!this.cabinetName()?.trim());
  hasCabinetPhone = computed(() => !!this.cabinetPhone()?.trim());
  hasCabinetEmail = computed(() => !!this.cabinetEmail()?.trim() && this.cabinetEmail()!.includes('@'));
  cabinetComplete = computed(() => this.hasCabinetName() && this.hasCabinetPhone() && this.hasCabinetEmail());
  canSend = computed(() => this.hasAccess() && this.cabinetComplete() && !this.sending());

  ngOnInit(): void {
    forkJoin({
      access: this.expertWalletService.checkAccess().pipe(catchError(() => of({hasAccess: false, plan: ''}))),
      settings: this.userService.getMySettings().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({access, settings}) => {
        this.hasAccess.set(!!access.hasAccess);
        this.cabinetName.set(settings?.cabinetName?.trim() || null);
        this.cabinetPhone.set(settings?.cabinetPhone?.trim() || null);
        this.cabinetEmail.set(settings?.cabinetEmail?.trim() || null);
        this.accessState.refresh();
        this.loading.set(false);
        if (access.hasAccess) {
          this.loadRecipientsPreview();
        }
      },
      error: () => {
        this.hasAccess.set(false);
        this.loading.set(false);
      },
    });
  }

  sendAll(): void {
    if (!this.canSend()) {
      if (!this.cabinetComplete()) {
        this.notificationService.error(
          'Renseignez le nom, le téléphone et l\'email du cabinet dans les paramètres avant d\'envoyer.',
          4000
        );
      }
      return;
    }
    this.sending.set(true);
    this.lastResult.set(null);
    this.expertWalletService.sendAll().subscribe({
      next: (result) => {
        this.sending.set(false);
        this.lastResult.set(result);
        this.loadRecipientsPreview();
        if (result.sent > 0) {
          this.notificationService.success(
            `${result.sent} email(s) envoyé(s) avec les QR Apple et Google Wallet.`,
            4000
          );
        } else if (result.skippedNoContact > 0 && result.failed === 0) {
          this.notificationService.error(
            'Aucun email envoyé : aucun client n\'a d\'adresse de contact.',
            4000
          );
        } else {
          this.notificationService.error('Aucun email n\'a pu être envoyé.', 4000);
        }
      },
      error: (err) => {
        this.sending.set(false);
        this.notificationService.error(
          httpErrorMessage(err, 'Impossible d\'envoyer les QR codes Wallet.'),
          4500
        );
      },
    });
  }

  private loadRecipientsPreview(): void {
    this.recipientsLoading.set(true);
    this.expertWalletService.recipientsPreview(10).subscribe({
      next: (recipients) => {
        this.recipientsPreview.set(recipients);
        this.recipientsLoading.set(false);
      },
      error: () => {
        this.recipientsPreview.set([]);
        this.recipientsLoading.set(false);
      },
    });
  }
}
